import React, { useState } from 'react';
import { LayoutDashboard, Clock, FileText, Users, Menu } from 'lucide-react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import ThemeInitializer from './components/ThemeInitializer';
import FontInitializer from './components/FontInitializer';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import ProtectedRoute from './components/ProtectedRoute';

// Pages
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import Shifts from './pages/Shifts';
import Invoices from './pages/Invoices';
import ServiceAgreements from './pages/ServiceAgreements';
import ServiceAgreementsList from './pages/ServiceAgreementsList';
import Services from './pages/Services';
import Settings from './pages/Settings';
import Pricing from './pages/Pricing';
import Auth from './pages/Auth';
import LandingPage from './pages/LandingPage';

function NavBar() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  async function handleLogout() {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      // Error handled silently
    }
  }

  if (!currentUser) {
    return null;
  }

  return (
    <nav className="navbar">
      <div className="nav-brand">
        <div className="navbar-logo-container">
          <img 
            src="/titlebarwhite.png" 
            alt="Easy Invoice" 
            className="navbar-logo-image"
          />
          <span className="beta-label">beta version</span>
        </div>
      </div>
      <button 
        className={`nav-hamburger ${menuOpen ? 'open' : ''}`}
        onClick={() => setMenuOpen(!menuOpen)}
        aria-label="Toggle navigation"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>
      <ul className={`nav-links ${menuOpen ? 'open' : ''}`}>
        <li><Link to="/" onClick={() => setMenuOpen(false)}>Dashboard</Link></li>
        <li><Link to="/clients" onClick={() => setMenuOpen(false)}>Clients</Link></li>
        <li><Link to="/shifts" onClick={() => setMenuOpen(false)}>Shifts</Link></li>
        <li><Link to="/invoices" onClick={() => setMenuOpen(false)}>Invoices</Link></li>
        <li><Link to="/service-agreements-list" onClick={() => setMenuOpen(false)}>Agreements</Link></li>
        <li><Link to="/services" onClick={() => setMenuOpen(false)}>Price Guide</Link></li>
        <li><Link to="/pricing" onClick={() => setMenuOpen(false)}>Pricing</Link></li>
        <li><Link to="/settings" onClick={() => setMenuOpen(false)}>Settings</Link></li>
        <li>
          <button onClick={handleLogout} className="btn btn-secondary btn-small">
            Logout
          </button>
        </li>
      </ul>
    </nav>
  );
}

function BottomNav() {
  const { currentUser, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);

  if (!currentUser) return null;

  const tabs = [
    { path: '/', icon: <LayoutDashboard size={20} />, label: 'Dashboard' },
    { path: '/shifts', icon: <Clock size={20} />, label: 'Shifts' },
    { path: '/invoices', icon: <FileText size={20} />, label: 'Invoices' },
    { path: '/clients', icon: <Users size={20} />, label: 'Clients' },
  ];

  const moreItems = [
    { path: '/service-agreements-list', label: 'Service Agreements' },
    { path: '/services', label: 'NDIS Price Guide' },
    { path: '/pricing', label: 'Pricing' },
    { path: '/settings', label: 'Settings' },
  ];

  const isMoreActive = moreItems.some(item => location.pathname === item.path);

  async function handleLogout() {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      // Error handled silently
    }
  }

  return (
    <>
      {moreOpen && <div className="bottom-nav-overlay" onClick={() => setMoreOpen(false)} />}
      <div className="bottom-nav">
        {tabs.map(tab => (
          <Link
            key={tab.path}
            to={tab.path}
            className={`bottom-nav-item${location.pathname === tab.path ? ' active' : ''}`}
            onClick={() => setMoreOpen(false)}
          >
            <span className="bottom-nav-icon">{tab.icon}</span>
            <span>{tab.label}</span>
          </Link>
        ))}
        <button
          className={`bottom-nav-item bottom-nav-more${isMoreActive ? ' active' : ''}`}
          onClick={() => setMoreOpen(!moreOpen)}
          aria-label="More options"
        >
          <span className="bottom-nav-icon"><Menu size={20} /></span>
          <span>More</span>
        </button>
        {moreOpen && (
          <div className="bottom-nav-menu">
            {moreItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                className={`bottom-nav-menu-item${location.pathname === item.path ? ' active' : ''}`}
                onClick={() => setMoreOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            <button className="bottom-nav-menu-item bottom-nav-logout" onClick={handleLogout}>
              Logout
            </button>
          </div>
        )}
      </div>
    </>
  );
}

function HomePage() {
  const { currentUser } = useAuth();
  return currentUser ? <Dashboard /> : <LandingPage />;
}

function AppContent() {
  const { currentUser } = useAuth();

  return (
    <div className="App">
      <ThemeInitializer />
      <FontInitializer />
      <NavBar />

      <main className={currentUser ? "main-content" : ""}>
        <Routes>
          <Route path="/login" element={<Auth />} />
          <Route path="/" element={<HomePage />} />
          <Route path="/clients" element={<ProtectedRoute><Clients /></ProtectedRoute>} />
          <Route path="/shifts" element={<ProtectedRoute><Shifts /></ProtectedRoute>} />
          <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
          <Route path="/service-agreements" element={<ProtectedRoute><ServiceAgreements /></ProtectedRoute>} />
          <Route path="/service-agreements-list" element={<ProtectedRoute><ServiceAgreementsList /></ProtectedRoute>} />
          <Route path="/services" element={<ProtectedRoute><Services /></ProtectedRoute>} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        </Routes>
      </main>
      <BottomNav />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <SubscriptionProvider>
          <AppContent />
        </SubscriptionProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
